import React, { useContext, useState } from "react";
import { Avatar, Chip, IconButton, List, ListItemAvatar, ListItemSecondaryAction, ListItemText } from "@mui/material";

import { makeStyles } from 'tss-react/mui';

import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useDate } from "../../hooks/useDate";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import { Tooltip } from "@mui/material";

import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";

import ListItemButton from "@mui/material/ListItemButton";

const useStyles = makeStyles()((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flex: 1,
    height: "100%",
    overflow: "hidden",
    borderRadius: 0,
    backgroundColor: theme.palette.background.paper,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 8px 8px 16px",
    backgroundColor: theme.palette.mode === "light" ? "#f0f2f5" : "#202c33",
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: 15,
  },
  chatList: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  listItem: {
    cursor: "pointer",
    borderLeft: "4px solid transparent",
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: 15,
    fontWeight: 600,
  },
  title: {
    fontWeight: 600,
  },
}));

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

export default function ChatList({
  chats,
  handleSelectChat,
  handleDeleteChat,
  handleEditChat,
  onNewChat,
  pageInfo,
  loading,
}) {
  const { classes, theme } = useStyles();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { datetimeToClient } = useDate();

  const [confirmationModal, setConfirmModalOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState({});

  const { id } = useParams();

  const goToMessages = async (chat) => {
    if (unreadMessages(chat) > 0) {
      try {
        await api.post(`/chats/${chat.id}/read`, { userId: user.id });
      } catch (err) {}
    }

    if (id !== chat.uuid) {
      navigate(`/chats/${chat.uuid}`);
      handleSelectChat(chat);
    }
  };

  const handleDelete = () => {
    handleDeleteChat(selectedChat);
  };

  const unreadMessages = (chat) => {
    const currentUser = chat.users.find((u) => u.userId === user.id);
    return currentUser.unreads;
  };

  const getPrimaryText = (chat) => {
    const mainText = chat.title;
    const unreads = unreadMessages(chat);
    return (
      <>
        <span className={classes.title}>{mainText}</span>
        {unreads > 0 && (
          <Chip
            size="small"
            style={{ marginLeft: 5, height: 18 }}
            label={unreads}
            color="secondary"
          />
        )}
      </>
    );
  };

  const getSecondaryText = (chat) => {
    return chat.lastMessage !== ""
      ? `${datetimeToClient(chat.updatedAt)}: ${chat.lastMessage}`
      : "";
  };

  const getItemStyle = (chat) => {
    const selected = chat.uuid === id;
    return {
      borderLeft: selected
        ? `4px solid ${theme.palette.primary.main}`
        : "4px solid transparent",
      backgroundColor: selected ? theme.palette.action.selected : "transparent",
    };
  };

  return (
    <>
      <ConfirmationModal
        title={"Eliminar Conversa"}
        open={confirmationModal}
        onClose={setConfirmModalOpen}
        onConfirm={handleDelete}
      >
        Esta acción no se puede revertir, ¿confirmar?
      </ConfirmationModal>
      <div className={classes.mainContainer}>
        <div className={classes.header}>
          <span className={classes.headerTitle}>Conversaciones</span>
          {typeof onNewChat === "function" && (
            <Tooltip title="Nueva conversación">
              <IconButton color="primary" size="small" onClick={onNewChat}>
                <AddCommentOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
        <div className={classes.chatList}>
          <List>
            {Array.isArray(chats) &&
              chats.length > 0 &&
              chats.map((chat, key) => (
                <ListItemButton
                  onClick={() => goToMessages(chat)}
                  key={key}
                  className={classes.listItem}
                  style={getItemStyle(chat)}>
                  <ListItemAvatar>
                    <Avatar className={classes.avatar}>
                      {getInitials(chat.title)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={getPrimaryText(chat)}
                    secondary={getSecondaryText(chat)}
                    secondaryTypographyProps={{ noWrap: true }}
                    style={{ marginRight: 40 }}
                  />
                  {chat.ownerId === user.id && (
                    <ListItemSecondaryAction>
                      <IconButton
                        onClick={() => {
                          goToMessages(chat).then(() => {
                            handleEditChat(chat);
                          });
                        }}
                        edge="end"
                        aria-label="edit"
                        size="small"
                        style={{ marginRight: 5 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setSelectedChat(chat);
                          setConfirmModalOpen(true);
                        }}
                        edge="end"
                        aria-label="delete"
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItemButton>
              ))}
          </List>
        </div>
      </div>
    </>
  );
}
